import { createClient, type Client } from '@openharness/client'
import type { User } from '@openharness/protocol'
import { Menu, PanelLeft, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { AuthProvider, useBrowserAuth } from './components/auth-provider'
import { ChatView } from './components/chat/chat-view'
import { focusComposer } from './components/chat/composer'
import { ClientProvider, useClient } from './components/client-provider'
import { ShortcutsSheet } from './components/shortcuts-sheet'
import { SIDEBAR_ID, Sidebar } from './components/sidebar'
import { ThemePreference } from './components/theme-preference'
import { Button } from './components/ui/button'
import { useAuthState } from './hooks/use-auth'
import { useModes } from './hooks/use-modes'
import { useSkills } from './hooks/use-skills'
import { useModels } from './hooks/use-models'
import { useNotice } from './hooks/use-notice'
import { useRoute } from './hooks/use-route'
import { useSessionTree } from './hooks/use-session-tree'
import { useSessions, type DeleteSessionResult } from './hooks/use-sessions'
import { useSettings } from './hooks/use-settings'
import { useShortcuts } from './hooks/use-shortcuts'
import { dismissNotice } from './lib/notice'
import { createBrowserAuthClient } from './lib/auth-client'
import { beginSessionCheck, signOutSession } from './lib/auth-store'
import { modelNameLookup, modelPriceLookup } from './lib/models'
import { navigate, routeToHash, type Route } from './lib/router'
import { DeviceScreen } from './screens/device-screen'
import { SettingsScreen } from './screens/settings-screen'
import { SignInScreen } from './screens/sign-in-screen'
import { StartScreen } from './screens/start-screen'

/** What the root takes. `client` is the seam every test uses. */
export interface AppProps {
  /**
   * The client to use. `main.tsx` passes the dev fake client in fake mode; a test passes
   * `createFakeClient()`. Omitted, the app builds the real one from the saved settings.
   */
  client?: Client | undefined
  /** Show that this run is on the fake client, not a server. */
  fakeClient?: boolean | undefined
}

/**
 * The app: one client, one auth client, one route, a sidebar and a screen.
 *
 * The client is built here, from the saved settings — which is also why saving the settings
 * screen changes the server immediately: the settings store notifies, this component
 * re-renders, and the client is rebuilt. An empty server URL means the page's own origin.
 * The Better Auth client is built from the same setting, because sign-in and the API have to
 * be the same server.
 */
export function App({ client: providedClient, fakeClient = false }: AppProps = {}) {
  const settings = useSettings()
  const client = useMemo(
    () => providedClient ?? createClient({ baseUrl: settings.serverUrl }),
    [providedClient, settings.serverUrl],
  )
  const auth = useMemo(() => createBrowserAuthClient(settings.serverUrl), [settings.serverUrl])

  const route = useRoute()

  return (
    <ClientProvider client={client}>
      <AuthProvider auth={auth}>
        <AppShell route={route} fakeClient={fakeClient} />
      </AuthProvider>
    </ClientProvider>
  )
}

/**
 * The authentication gate, and then the app.
 *
 * The session is a cookie the app cannot read (epic #65, A2), so the first thing that happens
 * is `client.me()` — and until it answers there is nothing to show. A 401, from that read or
 * from any later call (which is what `noteAuthenticationError` is for), puts the sign-in page
 * in place of everything else, and signing in puts the reader back on the route they were on,
 * which never moved.
 *
 * Nothing behind the gate is even mounted while it is shut: a signed-out browser has no
 * sidebar and no lists to load, because every one of them would answer 401. The device
 * approval page is the one route that is *reached* without a session, and the sign-in page is
 * what it renders — verifying a device code claims it for the signed-in session (A6), so
 * there is no approving anything before signing in.
 */
function AppShell({ route, fakeClient }: { route: Route; fakeClient: boolean }) {
  const client = useClient()
  const auth = useBrowserAuth()
  const authState = useAuthState(client)

  // Who the app is signed in as: the one read per client, and the reason a 401 from anywhere
  // lands on the sign-in page.
  useEffect(() => {
    void beginSessionCheck(client)
  }, [client])

  // The last client whose check answered "signed in", and the user it answered with.
  //
  // A settings save rebuilds the client (the `useMemo` in `App`), and the effect above re-runs
  // for the new one — which is right, because another server means another session. What must
  // not happen is the screen being *torn down* while that re-check runs: Settings keeps its
  // "Saved" confirmation in local state, and the "Checking your session…" branch below would
  // unmount it in the same tick it is set (issue #81). So a re-check on a rebuilt client keeps
  // the frame up with the previous user until the new client has answered — a signed-out
  // answer still puts the sign-in page in place, and the first check of all has nothing to
  // keep and shows the checking screen, as before.
  const lastSignedIn = useRef<{ client: Client; user: User } | null>(null)
  useEffect(() => {
    if (authState.status === 'signed-in') {
      lastSignedIn.current = { client, user: authState.user }
    }
  }, [authState, client])

  const onSignOut = useCallback((): void => {
    void signOutSession(client, auth)
  }, [client, auth])

  // Signed in *and* on the sign-in page — the reader followed a `#/signin` link, or the
  // server sent them back there after a social sign-in. Where they meant to go is in the
  // route; without one, home.
  useEffect(() => {
    if (authState.status === 'signed-in' && route.name === 'signin') {
      navigate(route.next ?? '#/')
    }
  }, [authState.status, route])

  if (authState.status === 'checking') {
    const previous = lastSignedIn.current
    if (previous === null || previous.client === client) {
      return (
        <CenteredScreen>
          <p role="status" className="text-sm text-muted-foreground">
            Checking your session…
          </p>
        </CenteredScreen>
      )
    }
    // The client was rebuilt under a signed-in app: a re-check, not a cold start (issue #81).
    return (
      <AppFrame route={route} fakeClient={fakeClient} user={previous.user} onSignOut={onSignOut} />
    )
  }

  if (authState.status === 'signed-out') {
    // The screen centres itself; there is nothing beside it.
    return <SignInScreen returnHash={signInReturnHash(route)} />
  }

  return (
    <AppFrame route={route} fakeClient={fakeClient} user={authState.user} onSignOut={onSignOut} />
  )
}

/**
 * The signed-in frame: one sidebar, two layouts.
 *
 * From `md` up it is the static column it has always been; below `md` the same panel is an
 * overlay drawer, opened from the top bar that only exists at that size. A phone-width chat
 * therefore gets the whole viewport, and the list is one tap away instead of occupying two
 * thirds of the screen.
 */
function AppFrame({
  route,
  fakeClient,
  user,
  onSignOut,
}: {
  route: Route
  fakeClient: boolean
  user: User
  onSignOut: () => void
}) {
  const client = useClient()
  const { sessions, loading, error, truncated, create, remove, forget } = useSessions(client)
  // The chats' children, for the sidebar's nesting (epic #340, S1/S3; #346): a child is a real
  // session the server never lists as a chat of its own, so the tree is read beside the list.
  const tree = useSessionTree(client, sessions)
  // The catalog is loaded once here, for the whole shell: the composer's model selector and
  // Settings' default-model picker offer it, and the sidebar and the chat header label
  // untitled sessions with its display names (#91).
  const catalog = useModels(client)
  // The reader's modes (#245, M6), loaded once here beside the catalog: the two model pickers
  // and the chat header all read the same copy, and Settings edits it in place.
  const modes = useModes(client)
  // The reader's imported skills (#349, K4; #353), loaded once here beside the catalog and the
  // modes: the composer's `/` menu (K7), the mode editor's skills override (K8) and Settings →
  // Skills all read the same copy, and every write updates it in place.
  const skills = useSkills(client)
  const nameOf = useMemo(() => modelNameLookup(catalog.models), [catalog.models])
  // The catalog's prices, for the costs a chat and its replies show (#247). Built beside the
  // names because they come from the same list and change together.
  const costOf = useMemo(() => modelPriceLookup(catalog.models), [catalog.models])
  // The shell's notice (epic #116, U5): "This chat was deleted." lands here after the chat it
  // was about is gone, so it outlives the screen that raised it.
  const notice = useNotice()

  // Deleting from wherever the action was: the row or the header goes away with the session,
  // and deleting the *open* chat leaves it — New chat is where a reader without a chat
  // belongs (U5).
  // The open chat's children are re-read when its tasks move (and when it is opened), so the
  // sidebar nests a helper the moment the chat shows its card. Stable by session: an effect in
  // `ChatView` depends on it, and a fresh closure per render would make it re-fire forever.
  const refreshOpenChatChildren = useCallback((): void => {
    if (route.name === 'chat') {
      tree.refresh(route.sessionId)
    }
    // `tree.refresh` and not `tree`: the object is rebuilt whenever the children it holds change,
    // and an identity that moved on every answer would be an effect that never settles.
  }, [route, tree.refresh])

  const deleteSession = useCallback(
    async (sessionId: string): Promise<DeleteSessionResult> => {
      const result = await remove(sessionId)
      if (result.ok && route.name === 'chat' && route.sessionId === sessionId) {
        navigate('#/new')
      }
      return result
    },
    [remove, route],
  )

  // A chat deleted somewhere else: its stream says so (U5). The row goes without a call of
  // our own, and an open chat leaves for New chat — the ChatView raises the notice.
  const forgetSession = useCallback(
    (sessionId: string): void => {
      forget(sessionId)
      if (route.name === 'chat' && route.sessionId === sessionId) {
        navigate('#/new')
      }
    },
    [forget, route],
  )

  const [drawerOpen, setDrawerOpen] = useState(false)
  // Whether the desktop column is put away (U10). Shell state rather than a stored
  // preference: it is a thing about this window's width more than about the reader, and the
  // mobile drawer — the same panel, another layout — has always been state too.
  const [collapsed, setCollapsed] = useState(false)
  // The shortcuts sheet (#212), open from `?` or Ctrl/⌘+/.
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const panelRef = useRef<HTMLElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(false)

  // The shell's shortcuts (issue #212). They are the ones that are about the *app* rather
  // than about a screen: where the reader is (New chat), what is around the content (the
  // sidebar) and what the app can tell them about itself (the sheet). The two that belong to
  // a message box — `/` puts the cursor in it and Escape stops the turn — are asked for here
  // and answered there, because only the box knows whether it exists and what it is doing.
  useShortcuts({
    'new-chat': () => {
      navigate('#/new')
    },
    'focus-composer': () => {
      focusComposer()
    },
    'toggle-sidebar': () => {
      // One key, two layouts: below `md` the sidebar is the drawer and above it the column
      // that can be put away (#211), so which of the two moves is decided by the same
      // breakpoint the `md:` variants are written against.
      if (isWideWindow()) {
        setCollapsed((current) => !current)
      } else {
        setDrawerOpen((current) => !current)
      }
    },
    'shortcuts-sheet': () => {
      setShortcutsOpen(true)
    },
  })

  const closeDrawer = useCallback((): void => {
    setDrawerOpen(false)
  }, [])

  // Going somewhere closes it: the drawer exists to pick a destination, and leaving it open
  // over the screen it just opened hides that screen on the size where it matters most.
  useEffect(() => {
    closeDrawer()
  }, [route, closeDrawer])

  // Escape closes it, as it does for any overlay.
  useEffect(() => {
    if (!drawerOpen) {
      return
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeDrawer()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [drawerOpen, closeDrawer])

  // Focus follows the drawer: into the panel when it opens, back to the button that opened it
  // when it closes — but never on the first render, which would steal focus from the page.
  useEffect(() => {
    if (drawerOpen) {
      panelRef.current?.focus()
    } else if (wasOpen.current) {
      menuButtonRef.current?.focus()
    }
    wasOpen.current = drawerOpen
  }, [drawerOpen])

  return (
    <>
      {/* The theme's one server reader and writer (#203): mounted with the signed-in frame,
          because preferences need a session, and rendered nowhere. */}
      <ThemePreference />

      {/* The shortcut list (#212). Mounted like the Add-provider dialog: Radix keeps the
          content out of the tree while it is closed, and the root stays up so Escape has
          somewhere to put the focus back to. */}
      <ShortcutsSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      {drawerOpen ? (
        <div
          data-slot="sidebar-backdrop"
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={closeDrawer}
        />
      ) : null}

      {/* The app shell **is** the viewport: exactly as tall as the visible area, clipped, and
          the positioned ancestor of everything inside it. Two properties, both load-bearing
          (#316):
          - `overflow-clip` — nothing a screen lays out can scroll the *document*. A page that
            can scroll is a page with a second, useless scrollbar over empty space: a scroll
            pane clips its own content, but an absolutely positioned element inside it whose
            containing block is further out — every `sr-only` span, the chat's streaming caret
            among them — is not clipped by that pane and used to extend the document's scroll
            area (the settings screen scrolled 2 400px into nothing; a streaming reply, 17 000).
            `clip` rather than `hidden`: `hidden` still lets the browser scroll the box to
            reveal a focused descendant, which would shift the whole app.
          - `relative` — so those elements are positioned *here*, inside the clip, rather than
            against the initial containing block beyond the shell.
          `h-dvh` is the dynamic viewport height (`100vh` on a phone is the tall viewport, and
          using it is how a page gets a scrollbar the reader cannot get rid of). */}
      <div data-slot="app-shell" className="relative flex h-dvh min-h-0 overflow-clip">
        <Sidebar
          sessions={sessions}
          loading={loading}
          error={error}
          truncated={truncated}
          activeSessionId={route.name === 'chat' ? route.sessionId : undefined}
          user={user}
          onSignOut={onSignOut}
          fakeClient={fakeClient}
          open={drawerOpen}
          collapsed={collapsed}
          onToggleCollapsed={() => {
            setCollapsed(true)
          }}
          onNavigate={closeDrawer}
          panelRef={panelRef}
          nameOf={nameOf}
          onDelete={deleteSession}
          childrenOf={tree.childrenOf}
        />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-2 border-b px-3 py-2 md:hidden">
            <Button
              ref={menuButtonRef}
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Navigation"
              aria-expanded={drawerOpen}
              aria-controls={SIDEBAR_ID}
              onClick={() => {
                setDrawerOpen((current) => !current)
              }}
            >
              <Menu aria-hidden="true" />
            </Button>
            <span className="truncate text-sm font-medium">openharness</span>
          </div>

          {/* The way back to a column that was put away (U10). It only exists from `md` up —
              below that the sidebar is the drawer, and the drawer is opened from the bar
              above — and only while the column is actually gone. */}
          {collapsed ? (
            <div className="hidden items-center gap-2 border-b px-3 py-2 md:flex">
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Show sidebar"
                onClick={() => {
                  setCollapsed(false)
                }}
              >
                <PanelLeft aria-hidden="true" />
              </Button>
              <span className="truncate text-sm font-medium">openharness</span>
            </div>
          ) : null}

          {notice === null ? null : (
            <div
              role="status"
              className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2 text-sm"
            >
              <span>{notice}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                aria-label="Dismiss notice"
                onClick={dismissNotice}
              >
                <X aria-hidden="true" />
              </Button>
            </div>
          )}

          {route.name === 'chat' ? (
            // Keyed by session: opening another chat mounts a fresh transcript and stream
            // rather than mutating one in place.
            <ChatView
              key={route.sessionId}
              sessionId={route.sessionId}
              nameOf={nameOf}
              costOf={costOf}
              catalog={catalog}
              modes={modes}
              skills={skills.skills}
              onDelete={deleteSession}
              onDeleted={forgetSession}
              // A chat that has just started a helper (or finished one) tells the sidebar, so the
              // group under its row appears while the reader is still in it (#346). The tree's own
              // read is silent and one-per-chat, so this is a re-read of one row rather than a
              // walk of the list.
              onTasksChanged={refreshOpenChatChildren}
            />
          ) : route.name === 'settings' ? (
            <SettingsScreen catalog={catalog} modes={modes} skills={skills} />
          ) : route.name === 'device' ? (
            <DeviceScreen userCode={route.userCode} />
          ) : route.name === 'signin' ? (
            // Signed in: the effect above is already moving the hash on; this is what the
            // screen shows for the frame or two that takes.
            <SignInScreen returnHash={signInReturnHash(route)} />
          ) : (
            // `#/` and `#/new` are the same screen (X5): New chat, or the first-run flow when
            // the account has no provider key. The Home screen is gone, and an old `#/` link —
            // the sidebar's own logo among them — lands where a reader meant to go.
            <StartScreen
              createSession={create}
              catalog={catalog}
              modes={modes}
              skills={skills.skills}
            />
          )}
        </main>
      </div>
    </>
  )
}

/** A screen with nothing else around it: the sign-in page, and the session check before it. */
function CenteredScreen({ children }: { children: ReactNode }) {
  return <div className="flex h-full min-h-0 items-center justify-center px-6">{children}</div>
}

/**
 * Whether the window is at least `md` wide — the shell's one JavaScript read of a CSS
 * breakpoint (#212).
 *
 * The sidebar shortcut has to move whichever of the two sidebars this window has, and only the
 * stylesheet knows which that is: below `md` the panel is the drawer, from `md` up it is the
 * column that can be put away (#211). `matchMedia` is the honest way to ask, because it is the
 * *same query* the `md:` variants are compiled from — Tailwind's default `md` is 48rem, 768px
 * at this app's root size, and this is the one place that number is written in JavaScript. A
 * window that is resized between keystrokes gets the new answer, since nothing is cached.
 *
 * Where there is no `matchMedia` at all — jsdom, and anything that is not a browser — the
 * answer is "not wide", the same reading `theme.ts` gives the system preference, and the
 * drawer branch is the one that still works with no layout to speak of.
 */
function isWideWindow(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: 768px)').matches
    : false
}

/**
 * Where signing in should return to.
 *
 * A `#/signin` route may carry one (`?next=`); anywhere else, the route the reader is on *is*
 * the place to come back to — and for a social sign-in that travels to the provider, the hash
 * is what the browser returns to (the page is a SPA, so the URL is the state).
 */
function signInReturnHash(route: Route): string {
  if (route.name === 'signin') {
    return route.next ?? '#/'
  }
  const hash = window.location.hash
  return hash === '' ? routeToHash(route) : hash
}
